import { normalize } from 'normalizr';

import type {
  ChatAnnotation,
  ChatChannel,
  ChatChannelEvent,
  ChatComment,
  ChatShare,
  ChatTask,
} from '@frontend/api';

import type { ActionsOf } from 'store/types';

import {
  annotationSchema,
  channelSchema,
  channelEventSchema,
  commentSchema,
  shareSchema,
  taskSchema,
} from './entities';
import type { ChatEntities } from './types';

export const types = {
  ANNOTATION_RECEIVE: 'chat/entities/ANNOTATION_RECEIVE',
  CHANNEL_RECEIVE: 'chat/entities/CHANNEL_RECEIVE',
  CHANNEL_EVENT_RECEIVE: 'chat/entities/CHANNEL_EVENT_RECEIVE',
  CHANNELS_RECEIVE: 'chat/entities/CHANNELS_RECEIVE',
  COMMENT_RECEIVE: 'chat/entities/COMMENT_RECEIVE',
  ENTITIES_STORE_INIT: 'chat/entities/ENTITIES_STORE_INIT',
  SHARE_RECEIVE: 'chat/entities/SHARE_RECEIVE',
  TASK_RECEIVE: 'chat/entities/TASK_RECEIVE',
} as const;

/**
 * normalizr only adds an entity key once it visits an entity of that type, so
 * nested entities (e.g. comments on an annotation with none) may be absent.
 */
type NormalizedEntities<
  Required extends keyof ChatEntities,
  Nested extends keyof ChatEntities = never,
> = Pick<ChatEntities, Required> & Partial<Pick<ChatEntities, Nested>>;

type AnnotationEntities = NormalizedEntities<
  'annotations',
  'comments' | 'tasks'
>;

type ChannelEntities = NormalizedEntities<
  never,
  'annotations' | 'channelEvents' | 'channels' | 'comments' | 'shares' | 'tasks'
>;

export const entitiesStore = {
  init: () => ({
    type: types.ENTITIES_STORE_INIT,
  }),
  receiveAnnotation: (payload: ChatAnnotation) => ({
    type: types.ANNOTATION_RECEIVE,
    payload: {
      ...normalize<ChatAnnotation, AnnotationEntities, number>(
        payload,
        annotationSchema,
      ),
    },
  }),
  receiveChannel: (payload: ChatChannel) => ({
    type: types.CHANNEL_RECEIVE,
    payload: {
      ...normalize<
        ChatChannel,
        ChannelEntities & NormalizedEntities<'channels'>,
        number
      >(payload, channelSchema),
    },
  }),
  receiveChannels: (payload: ChatChannel[]) => ({
    type: types.CHANNELS_RECEIVE,
    payload: {
      ...normalize<ChatChannel, ChannelEntities, number[]>(payload, [
        channelSchema,
      ]),
    },
  }),
  receiveComment: (payload: ChatComment) => ({
    type: types.COMMENT_RECEIVE,
    payload: {
      ...normalize<ChatComment, NormalizedEntities<'comments'>, number>(
        payload,
        commentSchema,
      ),
    },
  }),
  receiveShare: (payload: ChatShare) => ({
    type: types.SHARE_RECEIVE,
    payload: {
      ...normalize<ChatShare, NormalizedEntities<'shares'>, number>(
        payload,
        shareSchema,
      ),
    },
  }),
  receiveChannelEvent: (payload: ChatChannelEvent) => ({
    type: types.CHANNEL_EVENT_RECEIVE,
    payload: {
      ...normalize<
        ChatChannelEvent,
        NormalizedEntities<'channelEvents'>,
        number
      >(payload, channelEventSchema),
    },
  }),
  receiveTask: (payload: ChatTask) => ({
    type: types.TASK_RECEIVE,
    payload: {
      ...normalize<ChatTask, NormalizedEntities<'tasks'>, number>(
        payload,
        taskSchema,
      ),
    },
  }),
};

export type ChatEntitiesAction = ActionsOf<{
  entitiesStore: typeof entitiesStore;
}>;
